import { formatearFechaYHora } from "@/lib/fechas";
import type {
  ArchivoDeTerreno,
  AudioDeTerreno,
  CampoDeTerreno,
  FotoDeTerreno,
  ItemDeTerreno,
  RegistroDeTerreno,
  RegistroDeTerrenoDelInforme,
  SeccionDeTerreno,
} from "@/lib/terreno";

/* ---------------------------------------------------------------------------
   La lógica PURA de la pestaña "Registro de terreno": cuánto hay, qué deja
   pasar cada filtro, qué fotos recorre el visor y cómo se dicen las fechas.

   Vive FUERA del componente a propósito: la única forma de que la cuenta de un
   contador o la lista del visor esté mal sin que nada se vea roto es que
   ocurra dentro del árbol, donde un test no distingue una cifra buena de una
   cercana. Acá cada una se prueba con datos de mano.
--------------------------------------------------------------------------- */

export type FiltroDeTerreno = "todo" | "fotos" | "audios" | "textos";

export interface ConteoDeTerreno {
  fotos: number;
  audios: number;
  textos: number;
  /** Se cuenta APARTE: una coordenada no es "material" que se entregue a leer. */
  gps: number;
}

const SIN_CONTEO: ConteoDeTerreno = { fotos: 0, audios: 0, textos: 0, gps: 0 };

export function pluralizar(n: number, singular: string, plural: string): string {
  return `${n} ${n === 1 ? singular : plural}`;
}

/**
 * Todas las fotos de un registro o captura. Un API anterior a las fotos
 * múltiples no manda `fotos`: ahí vale su única `foto`. Es el único lugar que
 * conoce esa compatibilidad.
 */
export function fotosDelRegistro(r: RegistroDeTerreno): FotoDeTerreno[] {
  return r.fotos && r.fotos.length > 0 ? r.fotos : r.foto ? [r.foto] : [];
}

function contarRegistro(r: RegistroDeTerreno): ConteoDeTerreno {
  return {
    fotos: fotosDelRegistro(r).length,
    audios: r.audios.length,
    textos: r.texto ? 1 : 0,
    gps: 0,
  };
}

function sumar(a: ConteoDeTerreno, b: ConteoDeTerreno): ConteoDeTerreno {
  return {
    fotos: a.fotos + b.fotos,
    audios: a.audios + b.audios,
    textos: a.textos + b.textos,
    gps: a.gps + b.gps,
  };
}

function contarCampo(c: CampoDeTerreno): ConteoDeTerreno {
  return {
    fotos: c.fotos.length + (c.portada ? 1 : 0),
    audios: c.audios.length,
    textos: c.textos.length,
    gps: c.gps ? 1 : 0,
  };
}

/**
 * Lo que una sección trae, recorriendo campos, ítems y capturas.
 *
 * LA PORTADA CUENTA COMO FOTO, igual que en los totales que calcula el API: la
 * cifra del encabezado de la pestaña y la suma de las secciones tienen que ser
 * la misma o la pantalla se contradice sola.
 */
export function conteoDeLaSeccion(seccion: SeccionDeTerreno): ConteoDeTerreno {
  let total = SIN_CONTEO;

  for (const campo of seccion.campos) total = sumar(total, contarCampo(campo));
  for (const item of seccion.items) {
    for (const r of item.registros) total = sumar(total, contarRegistro(r));
  }
  for (const r of seccion.capturas) total = sumar(total, contarRegistro(r));

  return total;
}

/** Fotos + audios + textos: lo que cuenta el contador, sin la coordenada. */
export function totalDeMaterial(c: ConteoDeTerreno): number {
  return c.fotos + c.audios + c.textos;
}

/** Si la sección trae ALGO, coordenada incluida. Decide el estado "vacía". */
export function tieneContenido(c: ConteoDeTerreno): boolean {
  return totalDeMaterial(c) > 0 || c.gps > 0;
}

/**
 * "2 audios · 3 fotos · 1 texto", en el orden del diseño.
 *
 * Una sección con SOLO una coordenada la nombra en vez de decir "Sin
 * registros": el encargado sí mandó algo, y negarlo mandaría a pedírselo otra
 * vez.
 */
export function descripcionDelConteo(c: ConteoDeTerreno): string {
  const partes = [
    c.audios > 0 && pluralizar(c.audios, "audio", "audios"),
    c.fotos > 0 && pluralizar(c.fotos, "foto", "fotos"),
    c.textos > 0 && pluralizar(c.textos, "texto", "textos"),
    c.gps > 0 && pluralizar(c.gps, "ubicación GPS", "ubicaciones GPS"),
  ].filter(Boolean);

  return partes.length > 0 ? partes.join(" · ") : "Sin registros";
}

/** El conteo de cada segmento del filtro. NO depende del filtro activo. */
export function conteosPorFiltro(
  secciones: SeccionDeTerreno[],
): Record<FiltroDeTerreno, number> {
  const total = secciones.reduce((acc, s) => sumar(acc, conteoDeLaSeccion(s)), SIN_CONTEO);

  return {
    todo: totalDeMaterial(total),
    fotos: total.fotos,
    audios: total.audios,
    textos: total.textos,
  };
}

/**
 * Qué deja pasar cada filtro. La coordenada solo se ve bajo "Todo": no es foto,
 * ni audio, ni texto, así que ningún segmento específico la reclama.
 */
export function visibleConElFiltro(filtro: FiltroDeTerreno) {
  return {
    fotos: filtro === "todo" || filtro === "fotos",
    audios: filtro === "todo" || filtro === "audios",
    textos: filtro === "todo" || filtro === "textos",
    gps: filtro === "todo",
  };
}

export function registroVisible(r: RegistroDeTerreno, filtro: FiltroDeTerreno): boolean {
  const ve = visibleConElFiltro(filtro);

  return (
    (ve.fotos && fotosDelRegistro(r).length > 0) ||
    (ve.audios && r.audios.length > 0) ||
    (ve.textos && r.texto !== null)
  );
}

/**
 * Bajo "Todo" TODO campo se dibuja —uno vacío dice "Sin registros del
 * encargado"—; con un filtro puesto solo los que tienen algo de ese tipo.
 */
export function campoVisible(c: CampoDeTerreno, filtro: FiltroDeTerreno): boolean {
  if (filtro === "todo") return true;

  const ve = visibleConElFiltro(filtro);

  return (
    (ve.fotos && (c.fotos.length > 0 || c.portada !== null)) ||
    (ve.audios && c.audios.length > 0) ||
    (ve.textos && c.textos.length > 0)
  );
}

export function itemVisible(i: ItemDeTerreno, filtro: FiltroDeTerreno): boolean {
  return i.registros.some((r) => registroVisible(r, filtro));
}

/** "Foto · Audio · Texto": los tipos que trae un registro. */
export function tiposDelRegistro(r: RegistroDeTerreno): string {
  return [fotosDelRegistro(r).length > 0 && "Foto", r.audios.length > 0 && "Audio", r.texto && "Texto"]
    .filter(Boolean)
    .join(" · ");
}

/** "Registro 01": se numera por la posición entre TODOS los del ítem. */
export function numeroDeRegistro(indice: number): string {
  return `Registro ${String(indice + 1).padStart(2, "0")}`;
}

export interface FotoDelVisor {
  uuid: string;
  contexto: string;
  capturado_en: string | null;
  archivo: ArchivoDeTerreno;
  latitud: number | null;
  longitud: number | null;
}

export interface UbicacionDeLaFoto {
  texto: string;
  urlDelMapa: string;
}

/** El enlace al mapa de una coordenada: el mismo para el GPS y para cada foto. */
export function urlDelMapaDeTerreno(latitud: number, longitud: number): string {
  return `https://www.openstreetmap.org/?mlat=${latitud}&mlon=${longitud}#map=17/${latitud}/${longitud}`;
}

/**
 * Dónde se tomó una foto, o `null` si no se sabe. Las DOS coordenadas o
 * ninguna: la app nunca inventa una posición, así que una foto de galería o
 * tomada sin permiso de ubicación llega en `null` y la pantalla lo dice
 * ("Sin ubicación") en vez de callarlo. `0` es una coordenada válida.
 */
export function ubicacionDeLaFoto(foto: {
  latitud?: number | null;
  longitud?: number | null;
}): UbicacionDeLaFoto | null {
  const { latitud, longitud } = foto;
  if (typeof latitud !== "number" || typeof longitud !== "number") return null;

  return { texto: `${latitud}, ${longitud}`, urlDelMapa: urlDelMapaDeTerreno(latitud, longitud) };
}

/**
 * Las fotos que el visor recorre: TODAS las que la pantalla muestra ahora, en
 * el orden en que se dibujan (campos, ítems, capturas). Con un filtro que no
 * muestra fotos la lista queda vacía.
 *
 * "N de M" del visor sale de ESTA lista y no de un total aparte: si la cuenta
 * y el recorrido salieran de lugares distintos, la última foto diría "9 de 10"
 * y el botón siguiente no tendría a dónde ir.
 */
export function fotosDelVisor(
  secciones: SeccionDeTerreno[],
  filtro: FiltroDeTerreno,
): FotoDelVisor[] {
  if (!visibleConElFiltro(filtro).fotos) return [];

  const lista: FotoDelVisor[] = [];

  const agregar = (
    foto: {
      uuid: string;
      capturado_en: string | null;
      archivo: ArchivoDeTerreno | null;
      latitud?: number | null;
      longitud?: number | null;
    },
    contexto: string,
  ) => {
    // Sin archivo no hay nada que mostrar: no entra al recorrido.
    if (!foto.archivo) return;

    lista.push({
      uuid: foto.uuid,
      contexto,
      capturado_en: foto.capturado_en,
      archivo: foto.archivo,
      latitud: foto.latitud ?? null,
      longitud: foto.longitud ?? null,
    });
  };

  for (const seccion of secciones) {
    for (const campo of seccion.campos) {
      for (const f of campo.fotos) agregar(f, `${seccion.nombre} · ${campo.nombre}`);
      if (campo.portada) {
        agregar(campo.portada, `${seccion.nombre} · Portada`);
      }
    }

    for (const item of seccion.items) {
      item.registros.forEach((r, i) => {
        for (const fr of fotosDelRegistro(r)) {
          agregar(fr, `${seccion.nombre} · ${item.nombre} · ${numeroDeRegistro(i)}`);
        }
      });
    }

    seccion.capturas.forEach((r, i) => {
      for (const fr of fotosDelRegistro(r)) {
        agregar(fr, `${seccion.nombre} · ${numeroDeRegistro(i)}`);
      }
    });
  }

  return lista;
}

/** HEIC/HEIF: el navegador no los dibuja en un `<img>`; se ofrece la descarga. */
export function esFormatoSinVistaPrevia(mime: string): boolean {
  const m = mime.toLowerCase();

  return m === "image/heic" || m === "image/heif";
}

/** "09:10": la hora en que se capturó, en hora de Chile. */
export function horaDeCaptura(iso: string | null): string {
  if (!iso) return "—";

  const completa = formatearFechaYHora(iso); // "25-09-2026 15:40"

  return completa === "—" ? completa : completa.slice(-5);
}

/** "05-07-2026 · 09:12": cuándo llegó al servidor. */
export function momentoDeSincronizacion(iso: string | null): string {
  if (!iso) return "—";

  const completa = formatearFechaYHora(iso);

  return completa === "—" ? completa : completa.replace(" ", " · ");
}

/**
 * "04-07-2026 · 09:10 – 11:33"; si la visita cruza de día, los dos extremos
 * completos. Un solo día es el caso corriente y repetir la fecha sería ruido.
 */
export function rangoDeLaVisita(visita: RegistroDeTerrenoDelInforme["visita"]): string {
  if (!visita) return "—";

  const desde = momentoDeSincronizacion(visita.desde);
  const hasta = momentoDeSincronizacion(visita.hasta);
  const mismoDia = desde.slice(0, 10) === hasta.slice(0, 10);

  return `${desde} – ${mismoDia ? hasta.slice(-5) : hasta}`;
}

/** "0:38": minutos y segundos. Sin duración dice "0:00" en vez de inventarla. */
export function duracionDelAudio(ms: number | null): string {
  const segundos = Math.max(0, Math.round((ms ?? 0) / 1000));

  return `${Math.floor(segundos / 60)}:${String(segundos % 60).padStart(2, "0")}`;
}

/**
 * Lo que se dice mientras NO hay texto: transcribiendo, falló, o es una nota
 * anterior a la función. El motivo de un fallo viaja como código y se traduce
 * acá: el mensaje del proveedor no llega nunca a esta pantalla.
 */
const FRASE_DEL_FALLO: Record<string, string> = {
  formato_no_admitido: "El formato de este audio no se puede transcribir.",
  archivo_muy_grande: "Esta nota pesa más de lo que se puede transcribir.",
  transcripcion_agotada: "No se pudo transcribir esta nota. Se intentó varias veces.",
  // Los cuatro del proveedor. Los dos últimos se llegan a ver solo tras agotar
  // los reintentos, y no hay botón para volver a intentar: la frase manda al
  // administrador en vez de prometer un reintento que nadie va a hacer.
  proveedor_credencial:
    "No se pudo transcribir: el servicio de transcripción rechazó la credencial. Avisa al administrador.",
  proveedor_rechazo:
    "No se pudo transcribir: el servicio rechazó la solicitud. Avisa al administrador.",
  proveedor_cuota:
    "No se pudo transcribir: el servicio alcanzó su límite de uso. Avisa al administrador.",
  proveedor_caido:
    "No se pudo transcribir: el servicio de transcripción no respondió tras varios intentos.",
};

export function avisoDeTranscripcion(audio: AudioDeTerreno): string {
  switch (audio.transcripcion_estado) {
    case "pendiente":
      return "Transcribiendo…";
    case "fallida":
      return (
        FRASE_DEL_FALLO[audio.transcripcion_motivo ?? ""] ??
        "No se pudo transcribir esta nota."
      );
    default:
      // `null` (nota anterior a la función) y una `lista` sin texto: en los
      // dos casos no hay nada que mostrar ni nada en camino.
      return "Esta nota no tiene transcripción.";
  }
}
